/*
 * Quote-first navigation (decision 33 and spec section 14). Kept in its own tiny module so the admin
 * layout can use it without pulling in any screen.
 *
 *   Workspace: Dashboard, Quotes, Invoices, Clients        (no "Home" item; the logo goes to the dashboard)
 *   Manage:    Templates, Payments (setup), Team
 *   Reports:   Payments (the record of payments; the owner, behind the payments_ledger flag)
 *   Account:   Settings, Billing, Help & Tips
 *   Phone tab bar: Quotes, Invoices, Clients, More
 *
 * The booking-era items (Bookings, Services, Customers, Calendar Integration) are gone with the booking product.
 */
import { Banknote, BarChart3, Briefcase, CalendarDays, Wrench, CreditCard, Inbox, Sun, FileText, Lightbulb, LayoutDashboard, LayoutTemplate, Receipt, Settings, UserSquare2, Users, Clock } from 'lucide-react';
import type { ComponentType } from 'react';

export interface QuoteNavItem {
  key: string;
  /** Key under the `quotes` namespace, `nav.*`. */
  labelKey: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  /** Highlight for any path under href (a quote's detail keeps Quotes lit). */
  matchPrefix?: boolean;
}

export const QUOTE_NAV = {
  workspace: [
    { key: 'dashboard', labelKey: 'nav.dashboard', href: '/admin/dashboard', icon: LayoutDashboard, matchPrefix: true },
    { key: 'requests', labelKey: 'nav.requests', href: '/admin/requests', icon: Inbox, matchPrefix: true },
    { key: 'quotes', labelKey: 'nav.quotes', href: '/admin/quotes', icon: FileText, matchPrefix: true },
    { key: 'invoices', labelKey: 'nav.invoices', href: '/admin/invoices', icon: Receipt, matchPrefix: true },
    { key: 'clients', labelKey: 'nav.clients', href: '/admin/clients', icon: UserSquare2, matchPrefix: true },
    { key: 'jobs', labelKey: 'nav.jobs', href: '/admin/jobs', icon: Briefcase, matchPrefix: true },
    { key: 'schedule', labelKey: 'nav.schedule', href: '/admin/schedule', icon: CalendarDays, matchPrefix: true },
  ],
  manage: [
    { key: 'templates', labelKey: 'nav.templates', href: '/admin/templates', icon: LayoutTemplate, matchPrefix: true },
    { key: 'payments', labelKey: 'nav.payments', href: '/admin/payments', icon: Banknote, matchPrefix: true },
    { key: 'team', labelKey: 'nav.team', href: '/admin/team', icon: Users, matchPrefix: true },
    // the machines: the contract puts them under Manage; the office (staff) manages them too
    { key: 'equipment', labelKey: 'nav.equipment', href: '/admin/equipment', icon: Wrench, matchPrefix: true },
  ],
  // Reports: a group of its own, grown a page at a time (Payments first). Owner only, and behind the `payments_ledger` flag (the layout checks it).
  reports: [
    { key: 'reportsPayments', labelKey: 'nav.reportsPayments', href: '/admin/reports/payments', icon: BarChart3, matchPrefix: true },
    // behind the `timesheets` flag (the layout checks it)
    { key: 'reportsTimesheets', labelKey: 'nav.reportsTimesheets', href: '/admin/reports/timesheets', icon: Clock, matchPrefix: true },
  ],
  account: [
    { key: 'settings', labelKey: 'nav.settings', href: '/admin/settings', icon: Settings, matchPrefix: true },
    { key: 'billing', labelKey: 'nav.billing', href: '/admin/billing', icon: CreditCard, matchPrefix: true },
    { key: 'help', labelKey: 'nav.help', href: '/admin/tips', icon: Lightbulb, matchPrefix: true },
  ],
} satisfies Record<string, QuoteNavItem[]>;

/** Staff can create and send quotes (decision 6) but not manage templates, payments, team or billing; they do manage the equipment. */
export const QUOTE_NAV_STAFF = {
  workspace: QUOTE_NAV.workspace.filter((i) => ['requests', 'quotes', 'invoices', 'clients', 'jobs', 'schedule'].includes(i.key)),
  manage: QUOTE_NAV.manage.filter((i) => i.key === 'equipment'),
  reports: [] as QuoteNavItem[],
  // the profile tab: the booking-preferences tab is hidden in quote-first (the booking engine is switched off)
  account: [{ key: 'settings', labelKey: 'nav.settings', href: '/staff/settings?tab=myProfile', icon: Settings, matchPrefix: true }],
};

/** My day: the estimator's assessments, on the crew landing (`/crew`, which admits estimators). */
export const MY_DAY_ITEM: QuoteNavItem = { key: 'myDay', labelKey: 'nav.myDay', href: '/crew', icon: Sun, matchPrefix: true };

/** An estimator takes requests, writes quotes and visits clients: Requests, Quotes, Clients and My day, no invoices, payments, settings or team. */
export const QUOTE_NAV_ESTIMATOR = {
  workspace: [...QUOTE_NAV.workspace.filter((i) => ['requests', 'quotes', 'clients'].includes(i.key)), MY_DAY_ITEM],
  manage: [] as QuoteNavItem[],
  reports: [] as QuoteNavItem[],
  account: [] as QuoteNavItem[],
};

/** The sidebar of a signed-in role in the owner area. */
export function quoteNavForRole(role: string | undefined): { workspace: QuoteNavItem[]; manage: QuoteNavItem[]; reports: QuoteNavItem[]; account: QuoteNavItem[] } {
  if (role === 'admin') return QUOTE_NAV;
  if (role === 'estimator') return QUOTE_NAV_ESTIMATOR;
  return QUOTE_NAV_STAFF;
}

export const QUOTE_TABS = QUOTE_NAV.workspace.filter((i) => ['quotes', 'invoices', 'clients'].includes(i.key));

/** The phone tab bar of an estimator: no Invoices. */
export const QUOTE_TABS_ESTIMATOR = QUOTE_NAV_ESTIMATOR.workspace;

/** Title of the top bar for a quote-first route, as a `quotes` namespace key (or null when not one of ours). */
export function quoteRouteTitleKey(pathname: string): string | null {
  const all: QuoteNavItem[] = [...QUOTE_NAV.workspace, ...QUOTE_NAV.manage, ...QUOTE_NAV.reports, ...QUOTE_NAV.account];
  const hit = all.find((i) => pathname === i.href || pathname.startsWith(i.href + '/'));
  return hit ? hit.labelKey : null;
}

/** Routes whose phone screen has the floating +, and the ones that hide the tab bar and top chrome (editors). */
export const FAB_ROUTES = /^\/admin\/(quotes|invoices|clients|dashboard)\/?$/;
export const EDITOR_ROUTE = /^\/admin\/(quotes|invoices)\/(new|[^/]+\/edit)\/?$|^\/admin\/onboarding\/?$|^\/admin\/change-orders\/[^/]+\/edit\/?$|^\/admin\/jobs\/[^/]+\/change-orders\/new\/?$/;

